<template>
  <div v-if="user.can_manage_course_connections">
    <Container class="flex flex-column">
      <header v-if="courseConnectionActivated" class="mrgb+">
        <Media>
          <MediaBody>
            <div class="flex align-items-center">
              <h1 class="h3 mrgv0">
                {{ $t('courseConnection.title') }}
              </h1>
              <div v-if="loading" class="mrgl">
                <Loader size="small" />
              </div>
              <div v-else class="flex align-items-center">
                <Btn
                  v-if="Object.keys(courseConnectionsLocations).length === 0"
                  data-cy="add-station"
                  variant="primary"
                  class="mrgl"
                  @click="openStationActivationDialog"
                >
                  <Icon id="add"></Icon>
                  {{ $t('courseConnection.add') }}
                </Btn>
                <ActivatedStations v-else class="mrgl" />
              </div>
            </div>
          </MediaBody>
        </Media>
        <container
          v-if="Object.keys(courseConnectionsLocations).length === 0"
          class="description-container"
        >
          <h4 class="description-title">
            {{ $t('courseConnection.description') }}
          </h4>
          <img
            src="../assets/images/directory.svg"
            alt="directory"
            class="description-img"
          />
        </container>
        <container v-else>
          <ConnectionsFilters></ConnectionsFilters>
          <section class="refresh-line mrgt mrgb flex justify-content-between">
            <span>
              <span v-if="nextAutoRefresh">
                {{ $t('courseConnection.nextRefresh') }}:
                {{ new Date(nextAutoRefresh).toLocaleTimeString('fr-CH') }}
              </span>
            </span>
            <span>
              {{ $t('courseConnection.lastRefresh') }}:
              {{ lastRefresh.toLocaleTimeString('fr-CH') }}
              <Btn
                variant="icon"
                class="mrgl-"
                @click="fetchNextCoursesAndAutoRefreshTime()"
              >
                <Icon id="refresh" :scale="1.5" />
              </Btn>
            </span>
          </section>
          <section class="timeline flex justify-content-end">
            <h5 class="mrgt">
              <Icon id="clock"></Icon>
              <span>{{ $t('courseConnection.time') }}: {{ time }}</span>
            </h5>
          </section>
          <DataTable
            v-if="items != null"
            :items="items"
            :columns="columns"
            :row-color-rules="computeRowBackgroundColor"
            @selectionChanged="selectedCoursesChanged"
          >
            <template #drawer>
              <CoursesActions
                @blockCourses="blockSelectedCourses()"
                @releaseCourses="releaseSelectedCourses()"
                @delayDeparture="delay => delayDeparture(delay)"
              ></CoursesActions>
            </template>
            <template #h-startLocation>
              {{ $t('courseConnection.nextCourses.columns.startLocation') }}
            </template>
            <template #h-platform>
              {{ $t('courseConnection.nextCourses.columns.platform') }}
            </template>
            <template #h-line>
              {{ $t('courseConnection.nextCourses.columns.line') }}
            </template>
            <template #h-vehicle>
              {{ $t('courseConnection.nextCourses.columns.vehicle') }}
            </template>
            <template #h-region>
              {{ $t('courseConnection.nextCourses.columns.region') }}
            </template>
            <template #h-status>
              {{ $t('courseConnection.nextCourses.columns.status') }}
            </template>
            <template #h-departure>
              {{ $t('courseConnection.nextCourses.columns.departure') }}
            </template>
            <template v-slot:c-status="props">
              <div
                :class="{
                  'orange-status': props.row.item.status === 'orange',
                  'green-status': props.row.item.status === 'green',
                  'red-status': props.row.item.status === 'red',
                }"
              >
                <Icon v-if="props.row.item.status === 'red'" id="stop"></Icon>
                <Icon
                  v-if="props.row.item.status === 'orange'"
                  id="clock"
                ></Icon>
                <Icon v-if="props.row.item.status === 'green'" id="bus"></Icon>
                <span class="mrgl-">
                  {{ computeStatusCell(props.row.item) }}
                </span>
              </div>
            </template>
          </DataTable>
        </container>
      </header>
      <header v-else>
        <h1 class="h3 mrgv0">
          {{ $t('courseConnection.not-activated') }}
        </h1>
      </header>
    </Container>
    <ActivateStationDialog />
    <DisableStationDialog />
  </div>
  <div v-else class="mrg+">
    <h3>
      {{ $t('courseConnection.not-allowed') }}
    </h3>
  </div>
</template>

<script>
import ActivatedStations from '@/components/courseConnection/ActivatedStations.vue';
import DisableStationDialog from '@/components/courseConnection/DisableStationDialog.vue';
import CoursesActions from '@/components/courseConnection/CoursesActions.vue';
import { mapActions, mapState } from 'vuex';
import ActivateStationDialog from '@/components/courseConnection/ActivateStationDialog.vue';
import eventBus from '@/eventBus';
import Loader from '@/components/ui/Loader.vue';
import DataTable from '@/components/ui/DataTable.vue';
import ConnectionsFilters from '@/components/courseConnection/ConnectionsFilters';

export default {
  components: {
    ConnectionsFilters,
    Loader,
    DisableStationDialog,
    ActivatedStations,
    ActivateStationDialog,
    DataTable,
    CoursesActions,
  },
  data() {
    return {
      loading: false,
      columns: [
        'startLocation',
        'platform',
        'line',
        'vehicle',
        'region',
        'status',
        'departure',
      ],
      nextAutoRefresh: null,
      time: new Date().toLocaleTimeString('fr-CH'),
      currentTimeout: null,
      timeInterval: null,
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('settings', ['courseConnectionActivated']),
    ...mapState('courses', [
      'courseConnectionsLocations',
      'nextCourses',
      'selectedCourses',
      'lastRefresh',
      'selectedStationFilter',
    ]),
    items() {
      let nextCoursesToBeItems = Object.values(this.nextCourses);
      if (this.selectedStationFilter !== null) {
        nextCoursesToBeItems = nextCoursesToBeItems.filter(
          course => course.start_sloid === this.selectedStationFilter.value
        );
      }
      return nextCoursesToBeItems
        .map(course => {
          return {
            id: course.id,
            startLocation: course.start_location,
            platform: course.platform,
            line: course.line,
            vehicle: course.vehicle,
            region: course.region,
            status: this.computeStatus(course),
            delayMinutes: course.connection.delay_minutes,
            departure: new Date(course.course_start).toLocaleTimeString(
              navigator.language,
              {
                hour: '2-digit',
                minute: '2-digit',
              }
            ),
            departureTimestamp: course.course_start,
            delayedDepartureTimestamp: course.connection.delayed_course_start,
          };
        })
        .sort((a, b) => (a.departureTimestamp > b.departureTimestamp ? 1 : -1));
    },
  },
  mounted() {
    this.getStationsCandidates();
    this.fetchStationsAndCourses();
    this.timeInterval = setInterval(() => {
      this.time = new Date().toLocaleTimeString('fr-CH');
    }, 1000);

    eventBus.$on(
      'fetchNextCoursesAndAutoRefreshTime',
      this.fetchNextCoursesAndAutoRefreshTime
    );
  },
  beforeDestroy() {
    // The auto-refresh chain reschedules itself through getNextCourses() responses, so a
    // destroyed instance would keep polling forever without this teardown.
    this.tornDown = true;
    clearInterval(this.timeInterval);
    if (this.currentTimeout) clearTimeout(this.currentTimeout);
    eventBus.$off(
      'fetchNextCoursesAndAutoRefreshTime',
      this.fetchNextCoursesAndAutoRefreshTime
    );
  },
  methods: {
    ...mapActions('courses', [
      'getStationsCandidates',
      'getCourseConnectionsLocations',
      'getNextCourses',
      'setSelectedCourses',
      'blockSelectedCourses',
      'releaseSelectedCourses',
      'delayDeparture',
      'updateCourse',
    ]),
    fetchStationsAndCourses() {
      this.loading = true;
      Promise.all([
        this.getCourseConnectionsLocations(),
        this.fetchNextCoursesAndAutoRefreshTime(),
      ]).finally(() => {
        this.loading = false;
      });
    },
    openStationActivationDialog() {
      eventBus.$emit('toggleStationActivationDialog');
    },
    computeStatus(course) {
      if (course.connection.is_blocked) return 'red';
      if (course.connection.delay_minutes > 0) return 'orange';
      return 'green';
    },
    computeStatusCell(item) {
      switch (item.status) {
        case 'red':
          return this.$t('courseConnection.nextCourses.status.red');
          break;
        case 'orange':
          return this.$t('courseConnection.nextCourses.status.orange', {
            time: new Date(item.delayedDepartureTimestamp).toLocaleTimeString(
              navigator.language,
              {
                hour: '2-digit',
                minute: '2-digit',
              }
            ),
            delay: item.delayMinutes,
          });
          break;
        case 'green':
        default:
          return this.$t('courseConnection.nextCourses.status.green');
      }
    },
    computeNextAutoRefresh() {
      let nextCoursesPlannedToDepart = Object.values(this.nextCourses)
        .filter(course => course.connection.is_blocked === false)
        .map(course => {
          return course.connection.delayed_course_start || course.course_start;
        });
      return nextCoursesPlannedToDepart.length > 0
        ? Math.min.apply(Math, nextCoursesPlannedToDepart)
        : null;
    },
    computeRowBackgroundColor(status) {
      switch (status) {
        case 'red':
          return '#f7d6d4';
          break;
        case 'orange':
          return '#fff3cc';
          break;
        case 'green':
        default:
          return 'inherit';
      }
    },
    selectedCoursesChanged(courses) {
      this.setSelectedCourses(courses.map(c => c.item));
    },
    fetchNextCoursesAndAutoRefreshTime() {
      this.getNextCourses().then(() => {
        // An in-flight response can land after teardown and would re-arm the timeout chain
        if (this.tornDown) return;
        this.nextAutoRefresh = this.computeNextAutoRefresh();
        if (this.nextAutoRefresh !== null) {
          if (this.currentTimeout) clearTimeout(this.currentTimeout);
          let inHowManyTimeDoWeRefresh = this.nextAutoRefresh - Date.now();

          /**
           * The following lines avoid to create a loop during which, the
           * condition to auto-refresh stays true. It happened, probably, due
           * to a drift between the browser time and the server time.
           * How to deal? To retry only in 3 minutes if the next-auto refresh
           * calculated a timestamp in the past.
           */

          if (inHowManyTimeDoWeRefresh <= 0) {
            inHowManyTimeDoWeRefresh = 3 * 60 * 1000;
            this.nextAutoRefresh = Date.now() + inHowManyTimeDoWeRefresh;
          }
          this.currentTimeout = setTimeout(
            () => this.fetchNextCoursesAndAutoRefreshTime(),
            inHowManyTimeDoWeRefresh
          );
        }
      });
    },
  },
};
</script>
<style lang="scss">
.refresh-line {
  align-items: center;
  border-bottom: 1px solid $brand-900;
}

.timeline {
  border-top: 2px solid $brand-900;
  border-bottom: 2px solid $brand-900;
}

.red-status {
  color: $danger-600;
  background-color: inherit !important;
}

.orange-status {
  color: $warning-700;
  background-color: inherit !important;
}

.green-status {
  color: $brand-700;
  background-color: inherit !important;
}

.description-container {
  margin-top: 6em;
  margin-left: auto;
  margin-right: auto;
}

.description-title {
  text-align: center;
}

.description-img {
  margin-top: 3em;
  margin-left: auto;
  margin-right: auto;
}
</style>
